/**
 * Canonical user-facing copy for Stripe decline codes.
 *
 * These are surfaced in two places today:
 *   1. The charge-failure flow (PaymentResultsDialog) — the decline code is plumbed up from
 *      anduin's `/client/payment/v5/charge` error response at `extensions.details.stripeDeclineCode`.
 *   2. The payment-method setup flow (NewPaymentMethodFailedComponent) — Stripe.js returns the
 *      decline code directly on the client-side error.
 */

export const STRIPE_DECLINE_FALLBACK_MESSAGE = 'Your card has been declined. Please contact your bank or card issuer for more assistance.';

export const BANK_ACCOUNT_UNUSABLE_MESSAGE = 'This may be due to a block or restriction from your bank. Please double-check that your account information is correct, try a different account, or contact Aiwyn Support at support@aiwyn.ai for help resolving the issue.';

export const PAYMENT_METHOD_SKIP_VERIFICATION_FAILED_MESSAGE = 'The payment method\'s banking details failed validation. Please check the account and routing numbers provided.';

export const DO_NOT_HONOR_MESSAGE = 'Your bank or card issuer didn’t approve this request. This can happen when linking a payment method or attempting a payment due to verification or security restrictions. Please use a different payment method or contact your financial institution to ensure they are not blocking the request.';

/**
 * Map of Stripe decline codes to user-facing copy. Keys are the `decline_code` values Stripe returns
 * (https://stripe.com/docs/declines/codes). Codes present here always render the mapped string; codes
 * absent here fall back to `STRIPE_DECLINE_FALLBACK_MESSAGE`.
 */
export const STRIPE_DECLINE_MESSAGES: Record<string, string> = {
  generic_decline: STRIPE_DECLINE_FALLBACK_MESSAGE,
  card_declined: STRIPE_DECLINE_FALLBACK_MESSAGE,
  do_not_honor: DO_NOT_HONOR_MESSAGE,
  insufficient_funds: 'Your card was declined due to insufficient funds. Please use a different payment method or contact your bank.',
  // PP-3851: Stripe's guidance is to show a generic decline for lost/stolen, because the person holding the
  // card is not necessarily its owner, and confirming the report hands them a signal we do not want to give.
  // Deliberately byte-identical to the generic decline so the two cases are indistinguishable to the holder.
  lost_card: STRIPE_DECLINE_FALLBACK_MESSAGE,
  stolen_card: STRIPE_DECLINE_FALLBACK_MESSAGE,
  expired_card: 'Your card has expired. Please use a different payment method or contact your card issuer.',
  incorrect_cvc: 'Your card’s security code (CVC) is incorrect. Please check the code and try again.',
  processing_error: 'An error occurred while processing your card. Please try again in a moment or use a different payment method.',
  fraudulent: 'Your card has been declined. Please contact your bank or card issuer for more assistance.',
  authentication_required: 'Your card requires authentication to complete this payment. Please try again and follow the prompts from your bank.',
  authentication_not_supported: 'Your card does not support the authentication required for this payment. Please use a different payment method.',
  restricted_card: 'Your card cannot be used for this transaction. Please use a different payment method or contact your card issuer.',
  merchant_blacklist: 'Your card cannot be used for this transaction. Please use a different payment method or contact your card issuer.',
  contact_issuer: 'Please contact your card issuer for more information before attempting this payment again.',
  call_issuer: 'Please contact your card issuer for more information before attempting this payment again.',
};

/**
 * Resolve a user-facing message for a Stripe decline code.
 *
 * Resolution order:
 *   1. If `declineCode` matches a known entry in {@link STRIPE_DECLINE_MESSAGES}, return it.
 *   2. If a non-empty `fallback` string was provided, return it (typically the raw `error.message`
 *      from the backend).
 *   3. Otherwise return {@link STRIPE_DECLINE_FALLBACK_MESSAGE}.
 */
export function resolveDeclineMessage(declineCode: string | undefined, fallback?: string): string {
  if (declineCode && STRIPE_DECLINE_MESSAGES[declineCode]) {
    return STRIPE_DECLINE_MESSAGES[declineCode];
  }

  if (fallback) {
    return fallback;
  }

  return STRIPE_DECLINE_FALLBACK_MESSAGE;
}

/**
 * The instrument a prepayment was charged against, used to phrase the failure reason (PP-3635 design:
 * "if I use a card it should mention my card; ACH should mention my bank account"). `'payment method'`
 * is the neutral fallback for surfaces that don't know the method type (e.g. the `/payable` payload,
 * which carries no method type).
 */
export type PaymentInstrument = 'card' | 'bank account' | 'payment method';

/**
 * Short cause phrases that slot into the design banner — "Payment has failed due to ${reason}." (the
 * surrounding copy + "You may try again…" CTA live in the template, per design). Keyed by the stable
 * `MappedStripeErrorEnum` name anduin persists in `payment.decline_code` / returns on
 * `PrepaymentRetryResult.declineCode`. A function entry receives the {@link PaymentInstrument} so the
 * phrase can name the client's card vs bank account; a string entry is instrument-independent.
 *
 * NOTE: copy is FE-owned for this surface (design's templated form, not the backend's full-sentence
 * `MappedStripeErrorEnum.message`). DRAFT wording — pending design sign-off; trivially editable here.
 *
 * NOTE: `FRAUDULENT` and `MERCHANT_BLACKLIST` are deliberately absent. ART-8929 collapses both (and `LOST_CARD` /
 * `STOLEN_CARD`) to `CARD_DECLINED` before `prepaymentDeclineCode` leaves anduin, following Stripe's instruction to
 * present those four as a generic decline, so prepayment can never reach them here. `LOST_CARD` / `STOLEN_CARD` stay
 * mapped because other callers of {@link resolvePrepaymentDeclineReason} are not necessarily fed by the collapsed
 * field. `RESTRICTED_CARD` is not in the collapse set: Stripe asks for no suppression there.
 */
const PREPAYMENT_DECLINE_REASONS: Record<string, string | ((instrument: PaymentInstrument) => string)> = {
  // Generic — phrased around the instrument used.
  INSUFFICIENT_FUNDS: i =>
    i === 'bank account'
      ? 'insufficient funds in your bank account'
      : i === 'card'
        ? 'insufficient funds on your card'
        : 'insufficient funds',
  GENERIC_DECLINE: i => `your ${i} being declined`,
  PROCESSING_ERROR: 'a processing error',
  DO_NOT_HONOR: i => `your ${i === 'bank account' ? 'bank' : 'card issuer'} declining the charge`,
  CONTACT_ISSUER: i => `your ${i === 'bank account' ? 'bank' : 'card issuer'} needing to be contacted`,
  CALL_ISSUER: i => `your ${i === 'bank account' ? 'bank' : 'card issuer'} needing to be contacted`,

  // Card-only codes.
  CARD_DECLINED: 'your card being declined',
  EXPIRED_CARD: 'an expired card',
  INCORRECT_CVC: 'an incorrect security code (CVC)',
  INCORRECT_ZIP: 'a billing ZIP/postal code mismatch',
  CARD_VELOCITY_EXCEEDED: 'your card reaching a balance or transaction limit',
  // PP-3851: generic on purpose. See the lost_card/stolen_card note on STRIPE_DECLINE_MESSAGES above. The
  // phrase matches CARD_DECLINED exactly so a lost/stolen report reads as an ordinary decline.
  LOST_CARD: 'your card being declined',
  STOLEN_CARD: 'your card being declined',
  RESTRICTED_CARD: 'your card being restricted',
  AUTHENTICATION_REQUIRED: 'additional authentication being required',

  // Bank-debit (ACH/ACSS/BACS) codes.
  STRIPE_BANK_ACCOUNT_UNUSABLE: 'your bank account being unusable',
  PAYMENT_METHOD_BANK_ACCOUNT_BLOCKED: 'a block or dispute on your bank account',
  STRIPE_MICRODEPOSIT_VERIFICATION_FAILED: 'a micro-deposit verification mismatch',
  STRIPE_BANK_ACCOUNT_EXISTS: 'a problem with your bank account',
  IDEMPOTENCY_ERROR: 'multiple payment attempts in quick succession',
};

/** Reason shown when the decline code is absent or unmapped (keeps the banner grammatical). */
export const GENERIC_PREPAYMENT_DECLINE_REASON = 'an unexpected error';

/**
 * Resolve the short cause phrase for a prepayment failure (FIRM-10915), to slot into
 * "Payment has failed due to ${reason}." Maps the stable `declineCode` (the authoritative field) to a
 * design-approved phrase, named for the {@link instrument} used; unknown/absent codes degrade to
 * {@link GENERIC_PREPAYMENT_DECLINE_REASON}. The raw `failureReason` is deliberately NOT surfaced here —
 * it is the unfiltered Stripe message (`ChargeExecutorV1` stores `e.message`, request-id and all).
 */
export function resolvePrepaymentDeclineReason(
  declineCode: string | undefined,
  instrument: PaymentInstrument = 'payment method',
): string {
  const reason = declineCode ? PREPAYMENT_DECLINE_REASONS[declineCode] : undefined;
  if (typeof reason === 'function') {
    return reason(instrument);
  }
  return reason ?? GENERIC_PREPAYMENT_DECLINE_REASON;
}
